import { MinusCircleIcon, PlusCircleIcon } from "@heroicons/react/solid";
import { HttpError, useOne, useParse } from "@refinedev/core";
import { useState } from "react";
import { Link } from "react-router-dom";
import { ISeatmapTicketsForm, ISimpleEventDetail, ITicketsResponseData, SupportedLanguage } from "interfaces";
import Button from "components/button";
import { CreateEventStep } from "interfaces";
import { SeatLayout } from "./seatLayout";
import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { languageSchema } from "routes/event/schema";
import { z } from "zod";
import { Tickets } from "./tickets";
import { NoParentTicketSchema } from "./forms/schema";
// import { Tickets } from "./tickets";

enum Section {
  SEATMAP = "seatmap",
  INDIVIDUAL_TICKET = "tickets",
  NONE = "none",
}

const CreateTicketSchema = (languages: SupportedLanguage[]) => {
  return z.object({
    tickets: NoParentTicketSchema(languages),
  });
};

export const CreateSeatmapTicket = (props: {eventDetailData: ISimpleEventDetail, refetchEventDetail: () => void}) => {
  const parse = useParse();
  const { params } = parse();

  const { data, isFetched, refetch: fetchTickets } =
  useOne<ITicketsResponseData>({
    resource: "content/events",
    id: `${params?.id}/tickets`,
    meta: {
      resource: "data",
    },
    queryOptions: {
      enabled: !!(params?.id && params?.step),
    } as any,
  });

  const defaultTicketsData = data?.data ?? null

  return isFetched ? (
    <CreateSeatmapTicketForm
      defaultTicketsData={defaultTicketsData}
      fetchTickets={fetchTickets}
      {...props}
    />
  ) : <></>;
}

export const CreateSeatmapTicketForm = ({eventDetailData, defaultTicketsData, fetchTickets, refetchEventDetail}: {eventDetailData: ISimpleEventDetail, defaultTicketsData: ITicketsResponseData | null, fetchTickets: () => void, refetchEventDetail: () => void}) => {
  const [openSectionId, setOpenSectionId] = useState<Section>(Section.NONE);

  const parse = useParse();
  const { params } = parse();

  const methods = useForm<ISeatmapTicketsForm, HttpError, ISeatmapTicketsForm>({
    defaultValues: {
      tickets: defaultTicketsData?.tickets ?? [],
    },
    mode: "onChange",
    resolver: (values, context, options) => {
      const createResolver = zodResolver(
        CreateTicketSchema(eventDetailData.languages)
      );
      return createResolver(values, context, options);
    },
  });
  
  const _handleSubmitSection = (sectionId: Section) => {
    if (!openSectionId) return setOpenSectionId(sectionId);
    return setOpenSectionId(
      openSectionId === sectionId ? Section.NONE : sectionId
    );
  };

  return (
    <FormProvider {...methods}>
      <div className="flex flex-col h-full gap-y-2 sm:gap-y-4">
        <div className="box-container p-0">
          <div className="flex flex-col flex-wrap gap-y-3 border-b border-gray-200 py-4 px-4 sm:px-6 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex-1 shrink-0 text-sm">
              <h3 className="text-base font-semibold leading-6">
                Seat layout
              </h3>
              <p className="mt-2">* Set up a seat layout for tickets with specific seat selection</p>
              <p>* One seat layout per ticket type</p>
            </div>
            <button
              onClick={() => _handleSubmitSection(Section.SEATMAP)}
              type="button"
            >
              {openSectionId === Section.SEATMAP ? (
                <MinusCircleIcon className="w-6 h-6 text-brand-500" />
              ) : (
                <PlusCircleIcon className="w-6 h-6 text-brand-500" />
              )}
            </button>
          </div>
          {(openSectionId === Section.SEATMAP) ? (
            <SeatLayout  eventDetailData={eventDetailData}/>
          ) : <></>}
        </div>

        <div className="box-container p-0">
          <div className="flex flex-col flex-wrap gap-y-3 border-b border-gray-200 py-4 px-4 sm:px-6 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex-1 shrink-0">
              <h3 className="text-base font-semibold leading-6">
                Individual Tickets
              </h3>
            </div>
            <button
              onClick={() => _handleSubmitSection(Section.INDIVIDUAL_TICKET)}
              type="button"
            >
              {openSectionId === Section.INDIVIDUAL_TICKET ? (
                <MinusCircleIcon className="w-6 h-6 text-brand-500" />
              ) : (
                <PlusCircleIcon className="w-6 h-6 text-brand-500" />
              )}
            </button>
          </div>
          {openSectionId === Section.INDIVIDUAL_TICKET ? <Tickets eventDetailData={eventDetailData} fetchEventDetail={refetchEventDetail}  /> : <></>}
        </div>

        <div
          className="w-full mt-auto px-4 py-4 sm:px-6 bg-white sticky bottom-0 z-[2]"
          style={{ boxShadow: "rgba(0, 0, 0, 0.23) 0px -5px 5px -5px" }}
        >
          <div className="flex items-center justify-end gap-x-4">
            <Link to={`/events/attractions/${params?.id}/edit?step=${CreateEventStep.PARTICIPANT_TEMPLATE}`}>
              <Button className="w-20 bg-white text-brand-500 border border-solid border-brand-500 hover:bg-gray-50 active:bg-gray-100">
                Back
              </Button>
            </Link>
            <Link
              to={`/events/attractions/${params?.id}/edit?step=${CreateEventStep.ADDITIONAL_SETUP}`}
            >
              <Button
                className={`w-20 disabled:opacity-70`}
                disabled={eventDetailData.current_step < params?.step}
              >
                Next
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </FormProvider>
  );
};
